import React from 'react';
import { useListNumbersQuery } from '@/client/api/whatsapp/list-numbers.query';

export interface SenderLabel {
  /** Short method name: which WhatsApp integration this is. */
  method: 'Meta' | 'UltraMsg' | 'Evolution';
  /** The real phone number when the provider reported it, else the alias or id. */
  number: string;
  /** "Meta · +233 24 719 6555" — for option lists and tooltips. */
  text: string;
}

/**
 * A linked phone (UltraMsg, or an Evolution line `evolution:<name>`) rather
 * than a Meta number: free text, no templates, no 24-hour window, and files go
 * to one person at a time. `id` is what the pickers send as from_number.
 */
export const isLinkedNumber = (id?: string | null): boolean =>
  id === 'ultramsg' || Boolean(id?.startsWith('evolution:'));

/**
 * One lookup for "which of our numbers is this?", shared by every WhatsApp
 * screen so the inbox, the pickers and the logs all name a sender the same way.
 *
 * `id` may be a phone_number_id, an alias, or missing. Missing means the
 * record predates sender tracking, which was the default Meta number.
 */
export const useSenderLabel = () => {
  const { data } = useListNumbersQuery();
  const numbers = data?.data || [];

  return (id?: string | null): SenderLabel | null => {
    const number = id
      ? numbers.find((n) => n.phone_number_id === id || n.alias === id)
      : numbers.find((n) => n.default) || numbers.find((n) => !n.transport || n.transport === 'meta');
    if (!number) {
      if (!id) return null;
      const method = id.startsWith('evolution:') ? 'Evolution' : id === 'ultramsg' ? 'UltraMsg' : 'Meta';
      return { method, number: id, text: `${method} · ${id}` };
    }
    const method =
      number.transport === 'ultramsg' ? 'UltraMsg' : number.transport === 'evolution' ? 'Evolution' : 'Meta';
    // A line is known by its name; its number follows once it is linked.
    const shown =
      number.transport === 'evolution'
        ? [number.label, number.display_phone_number].filter(Boolean).join(' ') || number.phone_number_id
        : number.display_phone_number || number.alias || number.phone_number_id;
    return { method, number: shown, text: `${method} · ${shown}` };
  };
};

const styles: Record<SenderLabel['method'], string> = {
  Meta: 'bg-green-50 text-green-700 border-green-200',
  UltraMsg: 'bg-amber-50 text-amber-800 border-amber-200',
  Evolution: 'bg-sky-50 text-sky-800 border-sky-200',
};

/** The sender as a badge: method, then number. Renders nothing until numbers load. */
const NumberBadge: React.FC<{ id?: string | null; showNumber?: boolean }> = ({ id, showNumber = true }) => {
  const label = useSenderLabel()(id);
  if (!label) return null;
  return (
    <span
      title={label.text}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${styles[label.method]}`}
    >
      {label.method}
      {showNumber && <span className="font-normal">{label.number}</span>}
    </span>
  );
};

export default NumberBadge;
